import { Capacitor } from "@capacitor/core";
import { api } from "./api/client";
import { APP_TARGET, WEB_PUSH_PUBLIC_KEY } from "./config";

export const PUSH_EVENT = "private-menu:push";

export type PushEventDetail = {
  opened: boolean;
  type?: string;
  orderId?: string;
};
export type WebPushState =
  | "enabled"
  | "needs-permission"
  | "denied"
  | "install-required"
  | "misconfigured"
  | "unsupported";

const WEB_DEVICE_ID_KEY = "private-menu:web-push-device-id";
let activeMode: "web" | null = null;
let deviceId: string | null = null;

function emitPush(data: Record<string, unknown> | undefined, opened: boolean) {
  window.dispatchEvent(new CustomEvent<PushEventDetail>(PUSH_EVENT, {
    detail: {
      opened,
      type: typeof data?.type === "string" ? data.type : undefined,
      orderId: typeof data?.orderId === "string" ? data.orderId : undefined,
    },
  }));
}

function webMessageHandler(event: MessageEvent) {
  if (event.data?.type !== PUSH_EVENT) return;
  const payload = event.data.payload;
  emitPush(payload && typeof payload === "object" ? payload : undefined, Boolean(event.data.opened));
}

export function isStandalonePwa(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches
    || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

function isIosDevice(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function supportsWebPush(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function getWebPushState(): WebPushState {
  if (APP_TARGET !== "customer" || Capacitor.isNativePlatform()) return "unsupported";
  if (isIosDevice() && !isStandalonePwa()) return "install-required";
  if (!supportsWebPush()) return "unsupported";
  if (!WEB_PUSH_PUBLIC_KEY) return "misconfigured";
  if (Notification.permission === "granted") return "enabled";
  if (Notification.permission === "denied") return "denied";
  return "needs-permission";
}

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = globalThis.atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return navigator.serviceWorker.ready;
}

async function syncWebSubscription(registration: ServiceWorkerRegistration): Promise<void> {
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(WEB_PUSH_PUBLIC_KEY),
    });
  }
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) throw new Error("浏览器没有返回完整的推送订阅");
  const device = await api.registerDevice("web", subscription.endpoint, { p256dh, auth });
  deviceId = device.id;
  window.localStorage.setItem(WEB_DEVICE_ID_KEY, device.id);
}

async function startWebPush(): Promise<void> {
  if (APP_TARGET !== "customer" || !("serviceWorker" in navigator)) return;
  activeMode = "web";
  navigator.serviceWorker.addEventListener("message", webMessageHandler);
  const registration = await registerServiceWorker();
  deviceId = window.localStorage.getItem(WEB_DEVICE_ID_KEY);
  if (getWebPushState() === "enabled") await syncWebSubscription(registration);
}

export async function startPushNotifications(): Promise<void> {
  if (activeMode) return;
  if (Capacitor.isNativePlatform()) return;
  await startWebPush();
}

export async function enableWebPushNotifications(): Promise<WebPushState> {
  const current = getWebPushState();
  if (current === "install-required" || current === "unsupported" || current === "misconfigured") {
    return current;
  }
  const registration = await registerServiceWorker();
  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (permission !== "granted") return getWebPushState();
  await syncWebSubscription(registration);
  return "enabled";
}

export async function stopPushNotifications(): Promise<void> {
  if (activeMode === "web") {
    if (deviceId) await api.disableDevice(deviceId).catch(() => undefined);
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      await subscription?.unsubscribe().catch(() => undefined);
      navigator.serviceWorker.removeEventListener("message", webMessageHandler);
    }
    window.localStorage.removeItem(WEB_DEVICE_ID_KEY);
    deviceId = null;
    activeMode = null;
    return;
  }
}
